/**
 * Read-only SIM details panel displaying metadata with an optional edit action.
 *
 * @module components/sim/Details
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from "react";
import {connect} from "react-redux";
import {Divider, Toolbar, Typography} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import PropTypes from "prop-types";
import moment from "moment";
import {setEditSimDetailsStep} from "../../redux/sims/actions";
import {useDetailsStyle} from "./Details.js";

/**
 * A functional component that displays the details of the current simulation
 * and allows editing of its details if the `currentSim` object is provided.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.currentSim - The object containing details of the current simulation.
 * @param {Function} props.setEditSimDetailsStep - The function to invoke when the "Edit Details" option is clicked.
 * @param {string} props.title - The title to display at the top of the details view.
 *
 * @returns {JSX.Element | null} A JSX element displaying simulation details and an edit option if `currentSim` is provided, otherwise `null`.
 */
const Details = ({currentSim, setEditSimDetailsStep, title}) => {
    const classes = useDetailsStyle();

    if (currentSim) {
        return (
            <div>
                <Divider className={classes.divider}/>
                <Toolbar className={classes.root}>
                    <Typography className={classes.title} variant="h6" component="div">
                        {title}
                        <br/>
                        <Typography
                            variant="subtitle1"
                            className={classes.editDetails}
                            onClick={setEditSimDetailsStep}
                        >
                            <EditIcon className={classes.editIcon}/>
                            <strong>Edit Details</strong>
                        </Typography>
                    </Typography>
                    <Typography variant="caption" className={classes.userDetails}>
                        <strong>
                            {" "}
                            {currentSim.created_by} / {currentSim.company}{" "}
                        </strong>
                        <br/>
                        Product : <strong> {currentSim.product} </strong>| Created :{" "}
                        <strong>
                            {moment(new Date(currentSim.created_at)).format(
                                "MMM DD YYYY h:mm A"
                            )}
                        </strong>
                    </Typography>
                </Toolbar>
            </div>
        );
    }
    return null;
};

/**
 * PropTypes validation for the Details component.
 * Defines the expected data types of the props passed to the Details component.
 */
Details.propTypes = {
    currentSim: PropTypes.object,
    setEditSimDetailsStep: PropTypes.func,
    title: PropTypes.string,
};

/**
 * Maps the relevant portion of the application state to the component's props.
 * This function is used to link specific pieces of the Redux store's state to the props
 * required by a React component.
 *
 * @param {Object} state - The current state of the Redux store.
 * @returns {Object} An object containing the mapped state variables required by the component.
 *                   Specifically, it maps the `newSim` property from `state.sim`.
 */
const mapStateToProps = (state) => ({
    newSim: state.sim.newSim,
});

/**
 * An object that maps dispatchable actions to component props, allowing the component
 * to interact with the Redux store by dispatching actions.
 *
 * @typedef {Object} mapDispatchToProps
 *
 * @property {Function} setEditSimDetailsStep - A function that dispatches an action
 * to set the current step for editing simulation details in the Redux store.
 */
const mapDispatchToProps = {setEditSimDetailsStep};

export default connect(mapStateToProps, mapDispatchToProps)(Details);
